import React, { useState, useEffect, Fragment, useCallback } from 'react';
import DataTable from 'react-data-table-component';
import Notification from 'components/Notification';
import { Transition, Listbox } from '@headlessui/react';
import {
  createUser,
  deleteUser,
  editUser,
  getRoles,
  getUsers
} from 'utils/network/ServiceHelper';
import { useNavigate } from 'react-router';
import {
  STATUS_NULL,
  PAGINATION,
  SHOW_ALL_LIMIT,
  STATUS_UNAUTHORIZED,
  SUCCESS_MESSAGE
} from 'utils/constants';

export default function Users() {
  const navigate = useNavigate();
  const columns = [
    {
      name: 'Role',
      cell: function render(row) {
        return <>{row.role_data.name}</>;
      }
    },
    {
      name: 'Name',
      sortable: true,
      selector: 'name',
      cell: function render(row) {
        return <>{row.name}</>;
      }
    },
    {
      name: 'Division',
      sortable: true,
      selector: 'division',
      cell: function render(row) {
        return <>{row.division}</>;
      }
    },
    {
      name: 'Actions',
      cell: function renderActions(row) {
        return (
          <div className="w-full">
            <button
              className="p-1 text-center bg-gray-100 border rounded-lg leading-5 transition-colors duration-150 active:bg-purple-600 hover:bg-gray-300 focus:outline-none focus:shadow-outline-gray"
              onClick={handleShowDetail(row)}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-5 h-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                />
              </svg>
            </button>
            <button
              className="p-1 ml-2 text-center bg-gray-100 border rounded-lg leading-5 transition-colors duration-150 active:bg-purple-600 hover:bg-gray-300 focus:outline-none focus:shadow-outline-gray"
              onClick={handleCreateEdit(row)}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-5 h-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                />
              </svg>
            </button>
            <button
              className="p-1 ml-2 text-center bg-gray-100 border rounded-lg leading-5 transition-colors duration-150 active:bg-purple-600 hover:bg-gray-300 focus:outline-none focus:shadow-outline-gray"
              onClick={handleDelete(row)}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-5 h-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                />
              </svg>
            </button>
          </div>
        );
      }
    }
  ];

  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState([]);
  const [pagination, setPagination] = useState({
    page: PAGINATION.DEFAULT_PAGE,
    perPage: PAGINATION.DEFAULT_PER_PAGE,
    totalPage: PAGINATION.DEFAULT_TOTAL_PAGE,
    totalData: PAGINATION.DEFAULT_TOTAL_DATA
  });
  const [form, setForm] = useState({
    id: null,
    role: null,
    name: '',
    division: '',
    email: '',
    phone_number: '',
    password: '',
    conf_password: ''
  });
  const [createEdit, setCreateEdit] = useState(false);
  const [notification, setNotification] = useState({
    show: false,
    error: false,
    title: '',
    message: ''
  });
  const [showDelete, setShowDelete] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [sortProp, setSortProp] = useState({
    order: 'created_at',
    sort: 'desc'
  });
  const [password, setPassword] = useState({
    showPassword: false,
    showConfirmPassword: false
  });

  async function fetchRoleList() {
    const body = {
      limit: SHOW_ALL_LIMIT,
      offset: PAGINATION.DEFAULT_PAGE,
      keyword: PAGINATION.DEFAULT_KEYWORD,
      order: 'id',
      sort: 'asc'
    };

    const baseUrl = process.env.REACT_APP_BASE_URL;

    try {
      const res = await getRoles(baseUrl, body);

      if (res?.message === SUCCESS_MESSAGE) {
        setRoles(res.data);
        if (res.data?.length > 0 ?? false)
          setForm({
            ...form,
            role: res.data[0]
          });
      }

      if (res?.status === STATUS_UNAUTHORIZED) {
        localStorage.clear();
        navigate('/login', { replace: true });
        return;
      }

      const err = JSON.parse(await res.text());
      if (err) {
        // notif('Error', err.message, true);
        return;
      }

      // notif('Error', 'Network error. Please try again later!', true);
    } catch (error) {
      setIsLoading(false);
      // notif('Error', 'Network error. Please try again later!', true);
    }
  }

  useEffect(() => {
    fetchRoleList();
  }, []);

  async function fetchUserList(page, perPage) {
    const body = {
      limit: perPage || pagination.perPage,
      offset: page || pagination.page,
      keyword: '',
      order: sortProp.order,
      sort: sortProp.sort
    };

    const baseUrl = process.env.REACT_APP_BASE_URL;

    try {
      const res = await getUsers(baseUrl, body);
      setIsLoading(false);

      if (res?.message === SUCCESS_MESSAGE) {
        setUsers(res.data);
        setPagination({
          ...pagination,
          totalData: res.total_data,
          totalPage: res.total_page
        });

        return;
      }

      if (res?.status === STATUS_UNAUTHORIZED) {
        localStorage.clear();
        navigate('/login', { replace: true });
        return;
      }

      const err = JSON.parse(await res.text());
      if (err) {
        notif('Error', err.message, true);
        return;
      }

      notif('Error', 'Network error. Please try again later!', true);
    } catch (error) {
      notif('Error', 'Network error. Please try again later!', true);
    }
  }

  useEffect(() => {
    fetchUserList();
  }, [roles]);

  function handlePerRowsChange(newPerPage, page) {
    setPagination({
      ...pagination,
      page,
      perPage: newPerPage,
      totalPage: Math.ceil(roles / newPerPage)
    });

    fetchUserList(page, newPerPage);
  }

  function handlePageChange(page) {
    setPagination({
      ...pagination,
      page
    });

    fetchUserList(page);
  }

  const handleSort = useCallback((column, sortDirection) => {
    setSortProp({
      order: column.selector,
      sort: sortDirection
    });
  });

  let timeout;
  useEffect(() => {
    if (timeout) clearTimeout(timeout);

    timeout = setTimeout(() => {
      fetchUserList();
    }, 500);
  }, [sortProp]);

  const handleCreateEdit = (row) => () => {
    if (row)
      setForm({
        ...form,
        role: roles?.filter(function (x) {
          return x.id === row.role_data.id;
        })[0],
        name: row.name,
        division: row.division,
        email: row.email,
        phone_number: row.phone_number,
        password: row.password,
        conf_password: row.conf_password,
        id: row.id
      });
    setCreateEdit(true);
  };

  function closeCreateEdit() {
    setCreateEdit(false);
  }

  const handleShowDetail = (row) => () => {
    if (row)
      setForm({
        ...form,
        role: roles?.filter(function (x) {
          return x.id === row.role_data.id;
        })[0],
        name: row.name,
        division: row.division,
        email: row.email,
        phone_number: row.phone_number,
        id: row.id
      });
    setShowDetail(true);
  };

  function closeShowDetail() {
    setShowDetail(false);
  }

  const handleDelete = (row) => () => {
    setForm({ ...form, id: row.id });
    setShowDelete(true);
  };

  function closeDelete() {
    setShowDelete(false);
  }

  function handleChangeForm(e) {
    setForm({
      ...form,
      [e.target.name]: e.target.value
    });
  }

  function handleRoleChange(value) {
    setForm({
      ...form,
      role: value
    });
  }

  function togglePassword() {
    setPassword({
      ...password,
      showPassword: !password.showPassword
    });
  }

  function toggleConfirmPassword() {
    setPassword({
      ...password,
      showConfirmPassword: !password.showConfirmPassword
    });
  }

  function notif(title, message, isError = false, duration = 3000) {
    setNotification({
      show: true,
      title: title,
      message: message,
      error: isError
    });

    setTimeout(() => {
      setNotification({ ...notification, show: false });
    }, duration);
  }

  function closeNotification() {
    setNotification({ ...notification, show: false });
  }

  function isValid() {
    if (form.id == null) {
      return (
        form.role !== null &&
        form.name.length > 0 &&
        form.email.length > 0 &&
        form.password.length > 0 &&
        form.conf_password.length > 0
      );
    }

    return form.role !== null && form.name.length > 0 && form.email.length > 0;
  }

  useEffect(() => {
    if (!createEdit && !showDelete)
      setForm({
        ...form,
        role: roles.length > 0 ? roles[0] : null,
        name: '',
        division: '',
        email: '',
        phone_number: '',
        password: '',
        conf_password: '',
        id: null
      });
  }, [createEdit, showDelete]);

  async function handleCreateEditUser() {
    if (!isValid()) {
      notif('Error', 'Please fill all required field!', true);
      return;
    }

    try {
      const baseUrl = process.env.REACT_APP_BASE_URL;

      if (form.id) {
        // edit user
        form.role_id = parseInt(form.role.id);
        const res = await editUser(baseUrl, form);

        if (res?.message === SUCCESS_MESSAGE) {
          notif('Success', 'User successfully updated!');

          closeCreateEdit();

          setIsLoading(true);
          fetchUserList();
          return;
        }

        if (res?.status === STATUS_UNAUTHORIZED) {
          localStorage.clear();
          navigate('/login', { replace: true });
          return;
        }

        const err = JSON.parse(await res.text());
        if (err) {
          notif('Error', err.message, true);
          return;
        }

        notif('Error', 'Network error. Please try again later!', true);
      } else {
        // create user
        form.role_id = parseInt(form.role.id);
        const res = await createUser(baseUrl, form);

        if (res?.message === SUCCESS_MESSAGE) {
          notif('Success', 'User successfully created!');

          closeCreateEdit();

          setIsLoading(true);
          fetchUserList();
          return;
        }

        if (res?.status === STATUS_UNAUTHORIZED) {
          localStorage.clear();
          navigate('/login', { replace: true });
          return;
        }

        const err = JSON.parse(await res.text());
        if (err) {
          notif('Error', err.message, true);
          return;
        }

        notif('Error', 'Network error. Please try again later!', true);
      }
    } catch (error) {
      notif('Error', 'Network error. Please try again later!', true);
    }
  }

  async function handleDeleteUser() {
    const baseUrl = process.env.REACT_APP_BASE_URL;

    try {
      const res = await deleteUser(baseUrl, form.id);

      if (res?.message === SUCCESS_MESSAGE) {
        notif('Success', 'User successfully deleted!');

        closeDelete();

        setIsLoading(true);
        fetchUserList();
        return;
      }

      if (res?.status === STATUS_UNAUTHORIZED) {
        localStorage.clear();
        navigate('/login', { replace: true });
        return;
      }

      const err = JSON.parse(await res.text());
      if (err) {
        notif('Error', err.message, true);
        return;
      }

      notif('Error', 'Network error. Please try again later!', true);
    } catch (error) {
      return;
    }
  }

  return (
    <div>
      <div className="flex flex-row items-center justify-between">
        <h2 className="my-6 text-xl font-semibold text-gray-700 dark:text-gray-200">
          Users
        </h2>
        <button
          className="px-4 py-2 mt-3 text-sm font-medium text-center text-white border border-transparent rounded-lg leading-5 transition-colors duration-150 bg-secondary hover:bg-yellow-700 focus:outline-none focus:shadow-outline-purple"
          onClick={handleCreateEdit(null)}
        >
          Add New
        </button>
      </div>
      <DataTable
        columns={columns}
        data={users}
        responsive
        sortServer
        onSort={handleSort}
        progressPending={isLoading}
        pagination
        paginationServer
        paginationTotalRows={pagination.totalData}
        onChangeRowsPerPage={handlePerRowsChange}
        onChangePage={handlePageChange}
      />

      {/* Create Edit Section */}
      <Transition
        show={createEdit}
        enter="transition-all ease-in duration-100"
        enterFrom="opacity-0"
        enterTo="opacity-100"
      >
        <div className="fixed inset-0 z-20 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0">
              <div className="absolute inset-0 bg-gray-100 opacity-75"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen"></span>
            <div
              className="inline-block px-8 pt-5 pb-8 overflow-hidden text-left align-bottom bg-white rounded-lg shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full"
              role="dialog"
              aria-modal="true"
              aria-labelledby="modal-headline"
            >
              {form.id ? (
                <h3 className="mb-3 font-bold">Edit User</h3>
              ) : (
                <h3 className="mb-3 font-bold">Create User</h3>
              )}

              <div
                className="absolute cursor-pointer top-3 right-3"
                onClick={closeCreateEdit}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-6 h-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <Listbox value={form.role} onChange={handleRoleChange}>
                <div className="relative mt-1">
                  <Listbox.Button className="relative w-full py-2 pl-3 pr-10 text-left bg-white rounded-lg shadow-md cursor-default focus:outline-none focus-visible:ring-2 focus-visible:ring-opacity-75 focus-visible:ring-white focus-visible:ring-offset-orange-300 focus-visible:ring-offset-2 focus-visible:border-indigo-500 sm:text-sm">
                    <span className="block truncate">{form.role?.name}</span>
                    <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="w-5 h-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8 9l4-4 4 4m0 6l-4 4-4-4"
                        />
                      </svg>
                    </span>
                  </Listbox.Button>
                  <Transition
                    as={Fragment}
                    leave="transition ease-in duration-100"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                  >
                    <Listbox.Options className="absolute w-full py-1 mt-1 overflow-auto text-base bg-white shadow-lg rounded-md max-h-60 ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                      {roles.map((role, index) => (
                        <Listbox.Option
                          key={index}
                          className={({ active }) =>
                            `${
                              active
                                ? 'text-amber-900 bg-amber-100'
                                : 'text-gray-900'
                            }
                          cursor-default select-none relative py-2 pl-10 pr-4`
                          }
                          value={role}
                        >
                          {({ selected, active }) => (
                            <>
                              <span
                                className={`${
                                  selected ? 'font-medium' : 'font-normal'
                                } block truncate`}
                              >
                                {role.name}
                              </span>
                              {selected ? (
                                <span
                                  className={`${
                                    active ? 'text-amber-600' : 'text-amber-600'
                                  }
                                absolute inset-y-0 left-0 flex items-center pl-3`}
                                >
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="w-5 h-5"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M5 13l4 4L19 7"
                                    />
                                  </svg>
                                </span>
                              ) : null}
                            </>
                          )}
                        </Listbox.Option>
                      ))}
                    </Listbox.Options>
                  </Transition>
                </div>
              </Listbox>
              <label className="block mt-3 text-sm">
                <span className="text-gray-700 dark:text-gray-400">
                  Name <span className="text-red-500">*</span>
                </span>
                <input
                  className="block w-full px-3 py-2 mt-1 text-sm border appearance-none rounded-md dark:border-gray-600 dark:bg-gray-700 focus:border-purple-400 focus:outline-none focus:shadow-outline-purple dark:text-gray-300 dark:focus:shadow-outline-gray form-input"
                  placeholder="Administrator"
                  name="name"
                  value={form.name}
                  onChange={handleChangeForm}
                />
              </label>
              <label className="block mt-3 text-sm">
                <span className="text-gray-700 dark:text-gray-400">
                  Division
                </span>
                <input
                  className="block w-full px-3 py-2 mt-1 text-sm border appearance-none rounded-md dark:border-gray-600 dark:bg-gray-700 focus:border-purple-400 focus:outline-none focus:shadow-outline-purple dark:text-gray-300 dark:focus:shadow-outline-gray form-input"
                  placeholder="IT Division"
                  name="division"
                  value={form.division}
                  onChange={handleChangeForm}
                />
              </label>
              <label className="block mt-3 text-sm">
                <span className="text-gray-700 dark:text-gray-400">
                  Email <span className="text-red-500">*</span>
                </span>
                <input
                  className="block w-full px-3 py-2 mt-1 text-sm border appearance-none rounded-md dark:border-gray-600 dark:bg-gray-700 focus:border-purple-400 focus:outline-none focus:shadow-outline-purple dark:text-gray-300 dark:focus:shadow-outline-gray form-input"
                  placeholder="john@doe.com"
                  name="email"
                  value={form.email}
                  onChange={handleChangeForm}
                />
              </label>
              <label className="block mt-3 text-sm">
                <span className="text-gray-700 dark:text-gray-400">
                  Phone Number
                </span>
                <input
                  className="block w-full px-3 py-2 mt-1 text-sm border appearance-none rounded-md dark:border-gray-600 dark:bg-gray-700 focus:border-purple-400 focus:outline-none focus:shadow-outline-purple dark:text-gray-300 dark:focus:shadow-outline-gray form-input"
                  placeholder="+6280000000000"
                  name="phone_number"
                  value={form.phone_number}
                  onChange={handleChangeForm}
                />
              </label>
              {form.id === STATUS_NULL && (
                <label className="block mt-3 text-sm">
                  <span className="text-gray-700 dark:text-gray-400">
                    Password <span className="text-red-500">*</span>
                  </span>
                  <div className="relative">
                    <input
                      className="block w-full px-3 py-2 mt-1 text-sm border appearance-none rounded-md dark:border-gray-600 dark:bg-gray-700 focus:border-purple-400 focus:outline-none focus:shadow-outline-purple dark:text-gray-300 dark:focus:shadow-outline-gray form-input"
                      placeholder="********"
                      type={password.showPassword ? 'text' : 'password'}
                      name="password"
                      value={form.password}
                      onChange={handleChangeForm}
                    />
                    <div
                      className="absolute top-0 bottom-0 flex flex-col justify-center cursor-pointer right-2"
                      onClick={togglePassword}
                    >
                      {password.showPassword ? (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="w-6 h-6"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                          />
                        </svg>
                      ) : (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="w-6 h-6"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                          />
                        </svg>
                      )}
                    </div>
                  </div>
                </label>
              )}
              {form.id === STATUS_NULL && (
                <label className="block mt-3 text-sm">
                  <span className="text-gray-700 dark:text-gray-400">
                    Password Confirmation{' '}
                    <span className="text-red-500">*</span>
                  </span>
                  <div className="relative">
                    <input
                      className="block w-full px-3 py-2 mt-1 text-sm border appearance-none rounded-md dark:border-gray-600 dark:bg-gray-700 focus:border-purple-400 focus:outline-none focus:shadow-outline-purple dark:text-gray-300 dark:focus:shadow-outline-gray form-input"
                      placeholder="********"
                      type={password.showConfirmPassword ? 'text' : 'password'}
                      name="conf_password"
                      value={form.conf_password}
                      onChange={handleChangeForm}
                    />
                    <div
                      className="absolute top-0 bottom-0 flex flex-col justify-center cursor-pointer right-2"
                      onClick={toggleConfirmPassword}
                    >
                      {password.showConfirmPassword ? (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="w-6 h-6"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                          />
                        </svg>
                      ) : (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="w-6 h-6"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                          />
                        </svg>
                      )}
                    </div>
                  </div>
                </label>
              )}
              <div className="flex flex-row-reverse">
                <button
                  className="px-4 py-2 mt-3 text-sm font-medium text-center text-white border border-transparent rounded-lg leading-5 transition-colors duration-150 bg-secondary hover:bg-yellow-700 focus:outline-none focus:shadow-outline-purple"
                  onClick={handleCreateEditUser}
                >
                  {form.id ? 'Edit' : 'Create'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </Transition>

      {/* Delete Section */}
      <Transition
        show={showDelete}
        enter="transition-all ease-in duration-100"
        enterFrom="opacity-0"
        enterTo="opacity-100"
      >
        <div className="fixed inset-0 z-20 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0">
              <div className="absolute inset-0 bg-gray-100 opacity-75"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen"></span>
            <div
              className="inline-block p-8 overflow-hidden text-left align-bottom bg-white rounded-lg shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full"
              role="dialog"
              aria-modal="true"
              aria-labelledby="modal-headline"
            >
              <h3 className="font-bold">Delete Confirmation</h3>
              <div
                className="absolute cursor-pointer top-3 right-3"
                onClick={closeDelete}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-6 h-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <p>Are you sure to delete this item?</p>
              <div className="flex flex-row-reverse">
                <button
                  className="px-4 py-2 mt-3 text-sm font-medium text-center text-white border border-transparent rounded-lg leading-5 transition-colors duration-150 bg-secondary hover:bg-yellow-700 focus:outline-none focus:shadow-outline-purple"
                  onClick={closeDelete}
                >
                  Cancel
                </button>
                <button
                  className="px-4 py-2 mt-3 text-sm font-medium text-center border border-transparent rounded-lg leading-5 transition-colors duration-150 focus:outline-none focus:shadow-outline-purple"
                  onClick={handleDeleteUser}
                >
                  Yes, Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      </Transition>
      {/* Detail Section */}
      <Transition
        show={showDetail}
        enter="transition-all ease-in duration-100"
        enterFrom="opacity-0"
        enterTo="opacity-100"
      >
        <div className="fixed inset-0 z-20 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0">
              <div className="absolute inset-0 bg-gray-100 opacity-75"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen"></span>
            <div
              className="inline-block p-8 overflow-hidden text-left align-bottom bg-white rounded-lg shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full"
              role="dialog"
              aria-modal="true"
              aria-labelledby="modal-headline"
            >
              <h3 className="font-bold">Detail {form.name ? form.name : ''}</h3>
              <div
                className="absolute cursor-pointer top-3 right-3"
                onClick={closeShowDetail}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-6 h-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <table className="mt-5 table-auto">
                <tbody>
                  <tr>
                    <td className="w-1/2 font-semibold">Name</td>
                    <td className="py-2 pl-4">{form.name ? form.name : ''}</td>
                  </tr>
                  <tr>
                    <td className="w-1/2 font-semibold">Division</td>
                    <td className="py-2 pl-4">
                      {form.division ? form.division : ''}
                    </td>
                  </tr>
                  <tr>
                    <td className="w-1/2 font-semibold">Email</td>
                    <td className="py-2 pl-4">
                      {form.email ? form.email : ''}
                    </td>
                  </tr>
                  <tr>
                    <td className="w-1/2 font-semibold">Phone Number</td>
                    <td className="py-2 pl-4">
                      {form.phone_number ? form.phone_number : ''}
                    </td>
                  </tr>
                  <tr>
                    <td className="w-1/2 font-semibold">Role</td>
                    <td className="py-2 pl-4">
                      {form.role ? form.role.name : ''}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </Transition>

      <Notification
        notification={notification}
        closeNotification={closeNotification}
      />
    </div>
  );
}
